import type { AckPlan } from './ack-plan'
import { classifyAckFailure, type BulkAckPoOutcome } from './bulk-ack-model'
import type { VisionClient } from '@/lib/vision/client'

export type BulkAckPoStatus =
  | 'pending'
  | 'submitting'
  | 'success'
  | 'skipped'
  | 'needs_review'
  | 'retryable'
  | 'unknown'
  | 'failed'

export interface BulkAckPoJob {
  orderId: string
  externalOrderNumber: string
  plan: AckPlan
  idempotencyKey: string
  status: BulkAckPoStatus
  message?: string
  code?: string
}

export interface BulkAckSubmitProgress {
  jobs: BulkAckPoJob[]
  completed: number
  total: number
}

const DEFAULT_CONCURRENCY = 3

export async function runBulkAcknowledgements(
  client: Pick<VisionClient, 'createAcknowledgement'>,
  jobs: readonly BulkAckPoJob[],
  opts: {
    concurrency?: number
    onlyRetryable?: boolean
    onProgress?: (progress: BulkAckSubmitProgress) => void
    signal?: AbortSignal
  } = {},
): Promise<BulkAckPoJob[]> {
  const concurrency = Math.max(1, opts.concurrency ?? DEFAULT_CONCURRENCY)
  const next = jobs.map(job => ({ ...job }))
  const queue = next
    .map((job, index) => ({ job, index }))
    .filter(({ job }) =>
      opts.onlyRetryable ? job.status === 'retryable' : job.status === 'pending' || job.status === 'retryable',
    )

  let completed = next.filter(job =>
    ['success', 'skipped', 'unknown', 'failed', 'needs_review'].includes(job.status),
  ).length

  const emit = () =>
    opts.onProgress?.({
      jobs: next.map(job => ({ ...job })),
      completed,
      total: next.length,
    })

  emit()

  let cursor = 0
  async function worker() {
    while (cursor < queue.length) {
      if (opts.signal?.aborted) return
      const current = queue[cursor++]
      if (!current) return
      const { job, index } = current
      next[index] = { ...job, status: 'submitting', message: undefined }
      emit()
      try {
        await client.createAcknowledgement(
          job.externalOrderNumber,
          {
            lines: job.plan.lines,
            expectedRejectedLines: job.plan.rejected.map(line => line.itemSequenceNumber),
          },
          job.idempotencyKey,
        )
        next[index] = { ...job, status: 'success', message: 'Acknowledged' }
      } catch (error) {
        const outcome = classifyAckFailure(error)
        next[index] = applyOutcome(job, outcome)
      }
      completed += 1
      emit()
    }
  }

  await Promise.all(Array.from({ length: Math.min(concurrency, queue.length) }, () => worker()))
  return next
}

function applyOutcome(job: BulkAckPoJob, outcome: BulkAckPoOutcome): BulkAckPoJob {
  return {
    ...job,
    status: outcome.kind,
    message: outcome.message,
    code: outcome.code,
  }
}

export function freshIdempotencyKey(): string {
  return crypto.randomUUID()
}
